import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { FileStatusEnum, IFileData } from "Data/interfaces/ListagemArquivos/IListFilesResponse"
import { Icon } from "Components/UI"
import { Card, CardItem, Container, Hr, MoreActionsMenuStatusChange } from "./styles"
import { Popover } from "antd"
import MoverArquivos from "Pages/ListagemArquivos/MoverArquivos"

interface IMoreActionsDropdownContentProps {
  record: IFileData | null
  folderId?: string
  breadCrumbPreviousItemId: string | null
  onToggleContextMenuPreventClose: (value: boolean) => void
  onOpenFile: (file: IFileData | null, event: number) => void
  onToggleFollowFolder: (files: IFileData[]) => void
  onDownloadFile: (file: IFileData | null) => void
  onEditFile: (file: IFileData | null) => void
  onChangeStatusFile: (
    file: IFileData | null,
    status: FileStatusEnum,
    onClose: () => void
  ) => void
  onOpenHistoryDrawer: (fileData?: IFileData | null) => void
  onOpenModalExclusaoArquivos: (file?: IFileData | null) => void
  onCloseMoreActionsDropdown: () => void
}

const MoreActionsDropdownContent: FC<IMoreActionsDropdownContentProps> = ({
  record,
  folderId,
  breadCrumbPreviousItemId,
  onToggleContextMenuPreventClose,
  onOpenFile,
  onToggleFollowFolder,
  onDownloadFile,
  onEditFile,
  onChangeStatusFile,
  onOpenHistoryDrawer,
  onOpenModalExclusaoArquivos,
  onCloseMoreActionsDropdown,
}) => {
  const { t } = useTranslation(['listagemArquivos', 'common'])
  const [openMoreActionsDropdown, setOpenMoreActionsDropdown] = useState(false)
  const [openMoverArquivos, setOpenMoverArquivos] = useState(false)

  const handleDownloadFile = () => {
    onDownloadFile(record)
    onCloseMoreActionsDropdown()
  }

  const handleEditFile = () => {
    onEditFile(record)
    onCloseMoreActionsDropdown()
  }

  const handleToggleStatus = (status: FileStatusEnum) => {
    onChangeStatusFile(record, status, () => setOpenMoreActionsDropdown(false))
    onCloseMoreActionsDropdown()
  }

  const handleOpenMoverArquivos = (open: boolean) => {
    onToggleContextMenuPreventClose(open)
    setOpenMoverArquivos(open)
  }

  const handleCloseMoverArquivos = () => {
    onToggleContextMenuPreventClose(false)
    setOpenMoverArquivos(false)
  }

  const handleOpenHistoryDrawer = () => {
    onOpenHistoryDrawer(record)
    onCloseMoreActionsDropdown()
  }

  const handleOpenModalExclusaoArquivos = () => {
    onOpenModalExclusaoArquivos(record)
    onCloseMoreActionsDropdown()
  }

  return (
    <Container>
      {!record?.IsFolder && (
        <Card>
          <CardItem onClick={() => onOpenFile(record, 0)}>
            <span className="cardItemText">
              {t('listagemArquivos:contextMenu.openFile')}
            </span>
            <Icon icon="rightArrow" customSize={10} className="cardItemIcon" />
          </CardItem>
        </Card>
      )}
      {record?.IsFolder && (
        <>
          <Card>
            {record?.IsFollow ? (
              <CardItem onClick={() => onToggleFollowFolder([record])}>
                <Icon icon="eyeX" customSize={18} className="cardItemIcon" />
                <span className="cardItemText">
                  {t('listagemArquivos:toolbar.unfollowFolder')}
                </span>
              </CardItem>
            ) : (
              <CardItem onClick={() => onToggleFollowFolder([record])}>
                <Icon icon="visualizar" customSize={10} className="cardItemIcon" />
                <span className="cardItemText">
                  {t('listagemArquivos:toolbar.followFolder')}
                </span>
              </CardItem>
            )}
          </Card>
          <Hr />
        </>
      )}
      <Card>
        <CardItem onClick={handleDownloadFile}>
          <Icon icon="download" customSize={14} className="cardItemIcon" />
          <span className="cardItemText">
            {t('listagemArquivos:contextMenu.download')}
          </span>
        </CardItem>
        <CardItem onClick={handleEditFile}>
          <Icon icon="newEdit" customSize={18} className="cardItemIcon" />
          <span className="cardItemText">
            {t('common:edit')}
          </span>
        </CardItem>
        {!record?.IsFolder && (
          <>
            <Popover
              open={openMoreActionsDropdown}
              trigger="click"
              placement="left"
              className="moreActionsPopoverStatusChange"
              content={
                <MoreActionsMenuStatusChange>
                  <div
                    className="moreActionsOptions"
                    onClick={() => handleToggleStatus(FileStatusEnum.Bloqueado)}
                  >
                    {t('listagemArquivos:toolbar.blocked')}
                  </div>
                  <div
                    className="moreActionsOptions"
                    onClick={() => handleToggleStatus(FileStatusEnum.Liberado)}
                  >
                    {t('listagemArquivos:toolbar.released')}
                  </div>
                </MoreActionsMenuStatusChange>
              }
              onOpenChange={(open) => setOpenMoreActionsDropdown(open)}
              overlayInnerStyle={{ padding: 0 }}
            >
              <CardItem>
                <Icon icon="download" customSize={14} className="cardItemIcon" />
                <span className="cardItemText">
                  {t('listagemArquivos:toolbar.changeStatus')}
                </span>
              </CardItem>
            </Popover>
            <Popover
              open={openMoverArquivos}
              trigger="click"
              placement="left"
              className="moreActionsPopoverStatusChange"
              content={
                <MoverArquivos
                  folderId={folderId}
                  breadCrumbId={breadCrumbPreviousItemId}
                  fileId={record?.Identifier?.ApiId}
                  position={{
                    x: -208,
                    y: -225,
                  }}
                  toggleDropdown={handleCloseMoverArquivos}
                />
              }
              onOpenChange={handleOpenMoverArquivos}
              overlayInnerStyle={{ padding: 0 }}
            >
              <CardItem>
                <Icon icon="newEdit" customSize={18} className="cardItemIcon" />
                <span className="cardItemText">
                  {t('listagemArquivos:contextMenu.moveFile')}
                </span>
              </CardItem>
            </Popover>
          </>
        )}
      </Card>
      <Hr />
      <Card>
        <CardItem onClick={handleOpenHistoryDrawer}>
          <Icon icon="history" customSize={18} className="cardItemIcon" />
          <span className="cardItemText">
            {t('listagemArquivos:toolbar.history')}
          </span>
        </CardItem>
      </Card>
      <Hr />
      <Card>
        <CardItem onClick={handleOpenModalExclusaoArquivos}>
          <Icon icon="excluir" customSize={14} className="cardItemIcon" />
          <span className="cardItemText">
            {t('common:delete')}
          </span>
        </CardItem>
      </Card>
    </Container>
  )
}

export default MoreActionsDropdownContent
